import { StorageManager, StorageManagerDriver } from '../StorageManager';

import type { StorageManagerResParams } from '../StorageManager';
import type { ExpirationStoreItemsType } from './types';

class ExpirationStorageManager<TData> {
  private expirationInstance: StorageManagerResParams<ExpirationStoreItemsType<TData, keyof TData>>;
  get: <TKey extends keyof TData>(key: TKey) => Promise<TData[TKey] | null>;
  set: <TKey extends keyof TData>(
    key: TKey,
    value: TData[TKey],
    expire: boolean | number,
    callback?: (err: Error, value: TData[TKey]) => void,
  ) => Promise<TData[TKey]>;

  constructor(name: string, driver?: StorageManagerDriver) {
    const { instance } = new StorageManager<ExpirationStoreItemsType<TData, keyof TData>>({
      name,
      driver: driver || StorageManagerDriver.INDEXEDDB,
    });

    this.expirationInstance = instance;

    const { getItem, removeItem, setItem } = this.expirationInstance;

    this.get = async <TKey extends keyof ExpirationStoreItemsType<TData, keyof TData>>(
      key: TKey,
    ) => {
      const data = await getItem(key);

      if (!data) return data;

      const { expire, value } = data;

      if (typeof expire === 'number' && expire < Date.now()) {
        removeItem(key);
        return null;
      }

      return value as TData[TKey];
    };

    this.set = <TKey extends keyof ExpirationStoreItemsType<TData, keyof TData>>(
      key: TKey,
      value: TData[TKey],
      expire: boolean | number,
      callback?: (err: Error, value: TData[TKey]) => void,
    ) => {
      let nextExpire: number | boolean = false;

      if (expire && typeof expire === 'number') {
        nextExpire = Math.round(expire * 1000 + Date.now()); // * 1000 to use seconds
      }

      const storageObject = {
        value,
        expire: nextExpire,
      };
      const handleCallback = callback
        ? (err: Error, value: ExpirationStoreItemsType<TData, keyof TData>[TKey]) =>
            callback(err, value.value as TData[TKey])
        : undefined;

      return setItem(key, storageObject, handleCallback) as Promise<TData[TKey]>;
    };
  }
}

export default ExpirationStorageManager;
